import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface RateLimitStatus {
  remainingPercentage: number;
  requestsRemaining: number;
  resetTime: Date | null;
  resetTimeoutSeconds: number;  // New field: explicit seconds until reset
  isThrottled: boolean;
  dismissed: boolean;
}

@Injectable()
export class RateLimitUiService {
  private status$ = new BehaviorSubject<RateLimitStatus>({
    remainingPercentage: 100,
    requestsRemaining: Infinity,
    resetTime: null,
    resetTimeoutSeconds: 0,
    isThrottled: false,
    dismissed: false
  });
  
  // Track the last dismissal time
  private lastDismissalTime = 0;
  private readonly DISMISS_COOLDOWN = 5000; // 5-second cooldown

  getStatus() {
    return this.status$.asObservable();
  }
  
  getCurrentStatus(): RateLimitStatus {
    return this.status$.value;
  }

  /**
   * Update the rate limit status
   * @param remaining Number of requests remaining
   * @param limit Total request limit
   * @param reset Reset time as Date or null
   * @param resetTimeoutSeconds Explicit seconds until reset (useful when reset date is not accurate)
   * @param isThrottled Whether requests are being throttled
   */
  updateStatus(
    remaining: number, 
    limit: number, 
    reset: Date | null, 
    resetTimeoutSeconds?: number,
    isThrottled = false
  ) {
    console.log('RATE-LIMIT-SERVICE: Updating status', {
      remaining,
      limit,
      reset,
      resetTimeoutSeconds,
      isThrottled
    });
    
    const currentStatus = this.status$.value;
    const remainingPercentage = limit ? (remaining / limit) * 100 : 100;
    
    // Calculate seconds until reset if not provided explicitly
    let secondsUntilReset = resetTimeoutSeconds;
    if (!secondsUntilReset && reset) {
      const now = new Date().getTime();
      const resetTime = reset.getTime();
      secondsUntilReset = Math.max(0, Math.ceil((resetTime - now) / 1000));
    }
    
    // Default to 30 seconds if we don't have a valid value
    if (!secondsUntilReset || secondsUntilReset <= 0) {
      secondsUntilReset = 30;
    }
    
    // Check if user recently dismissed - respect dismissal for a cooldown period
    const now = Date.now();
    const isInDismissalCooldown = now - this.lastDismissalTime < this.DISMISS_COOLDOWN;
    
    // Determine if we should keep the dismissed state
    let dismissed = currentStatus.dismissed;
    
    // Only reset dismissed state if:
    // 1. We're not in the dismissal cooldown period, AND
    // 2. One of the following is true:
    //    a. We're getting throttled when we weren't before
    //    b. The percentage drops below 20% (critical)
    //    c. The percentage drops dramatically (by more than 20%)
    if (!isInDismissalCooldown && 
        ((!currentStatus.isThrottled && isThrottled) || 
         remainingPercentage < 20 || 
         (remainingPercentage < currentStatus.remainingPercentage - 20))) {
      console.log('RATE-LIMIT-SERVICE: Resetting dismissed state due to worsened conditions');
      dismissed = false;
    }
    
    this.status$.next({
      remainingPercentage,
      requestsRemaining: remaining,
      resetTime: reset,
      resetTimeoutSeconds: secondsUntilReset,
      isThrottled: isThrottled || remaining < limit * 0.1,
      dismissed
    });
  }
  
  dismissStatus() {
    const currentStatus = this.status$.value;
    console.log('RATE-LIMIT-SERVICE: Dismissing status');
    this.lastDismissalTime = Date.now(); // Track when we dismissed
    this.status$.next({
      ...currentStatus,
      dismissed: true
    });
  }

  /**
   * Simple method to simulate a rate limit for testing
   * @param severity How severe the rate limit should be (0-100)
   */
  simulateRateLimit(severity = 50) {
    // Calculate values based on severity
    const remaining = Math.max(0, 100 - severity);
    const limit = 100;
    
    // Calculate reset time (between 10-60 seconds based on severity)
    const resetSeconds = Math.max(10, Math.min(60, 60 - (severity * 0.5)));
    const resetTime = new Date(Date.now() + (resetSeconds * 1000));
    
    console.log('RATE-LIMIT-SERVICE: Simulating rate limit', {
      severity,
      remaining,
      resetSeconds,
      resetTime
    });
    
    this.updateStatus(
      remaining,
      limit,
      resetTime,
      Math.round(resetSeconds),
      severity > 70 // Throttle if severity > 70%
    );
  }
}


// WEBPACK FOOTER //
// ./src/app/core/services/rate-limit-ui.service.ts